import { useState, type FormEvent } from "react";

import { apiClient, type DepartmentSummary, type UserRole } from "../../api/client";
import { errorMessage } from "../admin/errors";
import { ConfirmationDialog } from "./ConfirmationDialog";
import type { ProfileDraft, UserSectionProps } from "./UserDetailDrawer";

export function ProfileSection({
  actorRole,
  departments,
  onUpdated,
  user,
}: UserSectionProps & {
  actorRole: "manager" | "owner";
  departments: DepartmentSummary[];
}) {
  const [draft, setDraft] = useState<ProfileDraft>({
    displayName: user.display_name,
    departmentId: user.primary_department_id ?? "",
    role: user.role,
  });
  const [confirmRole, setConfirmRole] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const selectableDepartments = departments.filter(
    (department) => department.is_active !== false || department.id === user.primary_department_id,
  );

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    await perform(async () => {
      const updated = await apiClient.updateUserProfile(user.id, {
        display_name: draft.displayName.trim(),
        primary_department_id: draft.departmentId || null,
      });
      setDraft((current) => ({
        ...current,
        displayName: updated.display_name,
        departmentId: updated.primary_department_id ?? "",
      }));
      onUpdated(updated);
      setMessage("资料已保存；部门变更会撤销目标账户的历史会话。");
    });
  }

  async function changeRole() {
    await perform(async () => {
      const updated = await apiClient.changeUserRole(user.id, draft.role);
      setDraft((current) => ({ ...current, role: updated.role }));
      onUpdated(updated);
      setConfirmRole(false);
      setMessage("角色已更新，目标账户的历史会话已撤销。");
    });
  }

  async function perform(action: () => Promise<void>) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
    } catch (caught) {
      setConfirmRole(false);
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="user-detail-section" aria-labelledby="profile-section-title">
      <div>
        <span className="eyebrow">资料</span>
        <h3 id="profile-section-title">账户资料与角色</h3>
      </div>
      <form className="user-detail-form" onSubmit={(event) => void saveProfile(event)}>
        <label>
          用户名（不可修改）
          <input disabled readOnly value={user.username} />
        </label>
        <label>
          显示名称
          <input
            maxLength={160}
            onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
            required
            value={draft.displayName}
          />
        </label>
        <label>
          主部门{user.role === "owner" ? "（可选）" : "（必选）"}
          <select
            onChange={(event) => setDraft({ ...draft, departmentId: event.target.value })}
            required={user.role !== "owner"}
            value={draft.departmentId}
          >
            <option value="">请选择部门</option>
            {selectableDepartments.map((department) => (
              <option
                key={department.id}
                disabled={department.is_active === false}
                value={department.id}
              >
                {department.name} · {department.code}
                {department.is_active === false ? " · 已停用" : ""}
              </option>
            ))}
          </select>
        </label>
        <button disabled={busy} type="submit">
          保存资料
        </button>
      </form>
      <div className="user-role-control">
        <label>
          角色（需确认）
          <select
            disabled={actorRole === "manager"}
            onChange={(event) => setDraft({ ...draft, role: event.target.value as UserRole })}
            value={draft.role}
          >
            <option value="user">user</option>
            {actorRole === "owner" && <option value="manager">manager</option>}
            {actorRole === "owner" && <option value="owner">owner</option>}
          </select>
        </label>
        <button
          disabled={busy || actorRole === "manager" || draft.role === user.role}
          onClick={() => setConfirmRole(true)}
          type="button"
        >
          更改角色
        </button>
      </div>
      {message && <p className="status-banner">{message}</p>}
      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {confirmRole && (
        <ConfirmationDialog
          busy={busy}
          confirmLabel="确认更改角色"
          description={`将 ${user.username} 的角色从 ${user.role} 更改为 ${draft.role}，并立即撤销其历史会话。`}
          onCancel={() => setConfirmRole(false)}
          onConfirm={() => void changeRole()}
          title="确认角色变更"
        />
      )}
    </section>
  );
}
